import React, { useEffect, useMemo, useState } from "react";
import { api, formatDate, formatTRY, todayISODate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Pencil, Trash2, ArrowDownCircle, ArrowUpCircle, Landmark } from "lucide-react";
import { toast } from "sonner";

const empty = {
  direction: "borrowed",
  counterparty: "",
  amount: 0,
  paid_amount: 0,
  fully_paid: false,
  date: todayISODate(),
  note: "",
};

export default function Finans() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => setItems((await api.get("/loans")).data);
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const openNew = (direction = "borrowed") => {
    setEditing(null);
    setForm({ ...empty, direction });
    setOpen(true);
  };

  const openEdit = (ln) => {
    const total = Number(ln.amount) || 0;
    const paid = Number(ln.paid_amount) || 0;
    setEditing(ln);
    setForm({
      direction: ln.direction || "borrowed",
      counterparty: ln.counterparty || "",
      amount: total,
      paid_amount: paid,
      fully_paid: paid >= total - 0.01 && total > 0,
      date: (ln.date || todayISODate()).slice(0, 10),
      note: ln.note || "",
    });
    setOpen(true);
  };

  const setField = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "amount" && prev.fully_paid) next.paid_amount = Number(value) || 0;
      if (field === "fully_paid" && value) next.paid_amount = Number(prev.amount) || 0;
      return next;
    });
  };

  const save = async () => {
    if (!form.counterparty.trim()) { toast.error("Kişi/firma girin"); return; }
    if (!(Number(form.amount) > 0)) { toast.error("Tutar girin"); return; }
    const total = Number(form.amount) || 0;
    const paid = form.fully_paid ? total : Math.max(0, Math.min(Number(form.paid_amount) || 0, total));
    const payload = {
      direction: form.direction,
      counterparty: form.counterparty,
      amount: total,
      paid_amount: paid,
      date: form.date,
      note: form.note,
    };
    try {
      if (editing) { await api.put(`/loans/${editing.id}`, payload); toast.success("Güncellendi"); }
      else { await api.post("/loans", payload); toast.success("Kaydedildi"); }
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/loans/${deleteId}`);
      toast.success("Silindi");
      setDeleteId(null); load();
    } catch (e) {
      toast.error("Silinemedi");
    }
  };

  // Filtrele
  const filtered = items.filter((ln) => {
    if (tab === "borrowed") return ln.direction === "borrowed";
    if (tab === "lent") return ln.direction === "lent";
    return true;
  });

  const totals = useMemo(() => {
    const borrowed = items.filter((i) => i.direction === "borrowed");
    const lent = items.filter((i) => i.direction === "lent");
    const borrowedDebt = borrowed.reduce((s, i) => s + Math.max((Number(i.amount) || 0) - (Number(i.paid_amount) || 0), 0), 0);
    const lentPending = lent.reduce((s, i) => s + Math.max((Number(i.amount) || 0) - (Number(i.paid_amount) || 0), 0), 0);
    return { borrowedDebt, lentPending };
  }, [items]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Finans · Borç / Alacak</h1>
          <p className="text-sm text-slate-500">Dükkan borç aldığı ve verdiği tutarların takibi. Kısmi ödeme desteklenir.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="add-borrowed-btn" onClick={() => openNew("borrowed")} className="bg-rose-600 hover:bg-rose-700 text-white">
            <ArrowDownCircle className="w-4 h-4 mr-2" /> Borç Aldık
          </Button>
          <Button data-testid="add-lent-btn" onClick={() => openNew("lent")} className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <ArrowUpCircle className="w-4 h-4 mr-2" /> Borç Verdik
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-5">
          <div className="flex items-start gap-3">
            <ArrowDownCircle className="w-5 h-5 text-rose-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-[11px] uppercase tracking-widest text-rose-800 font-semibold">Dükkanın Borcu (Kalan)</p>
              <p className="font-display text-2xl font-bold text-rose-700 mt-1 font-mono-num">{formatTRY(totals.borrowedDebt)}</p>
              <p className="text-xs text-rose-700 mt-1">Bize verilen borçlardan geri ödemediğimiz tutar</p>
            </div>
          </div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5">
          <div className="flex items-start gap-3">
            <ArrowUpCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-[11px] uppercase tracking-widest text-emerald-800 font-semibold">Dükkanın Alacağı (Kalan)</p>
              <p className="font-display text-2xl font-bold text-emerald-700 mt-1 font-mono-num">{formatTRY(totals.lentPending)}</p>
              <p className="text-xs text-emerald-700 mt-1">Verdiğimiz borçlardan geri alamadığımız tutar</p>
            </div>
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all">Tümü</TabsTrigger>
          <TabsTrigger value="borrowed" data-testid="tab-borrowed">Alınan Borçlar</TabsTrigger>
          <TabsTrigger value="lent" data-testid="tab-lent">Verilen Borçlar</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-600 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-2.5 font-semibold">Tarih</th>
              <th className="text-left px-4 py-2.5 font-semibold">Tip</th>
              <th className="text-left px-4 py-2.5 font-semibold">Kişi / Firma</th>
              <th className="text-right px-4 py-2.5 font-semibold">Tutar</th>
              <th className="text-right px-4 py-2.5 font-semibold">Ödenen</th>
              <th className="text-right px-4 py-2.5 font-semibold">Kalan</th>
              <th className="text-left px-4 py-2.5 font-semibold">Not</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-slate-500 text-sm" data-testid="finans-empty">Kayıt yok.</td>
              </tr>
            )}
            {filtered.map((ln) => {
              const total = Number(ln.amount) || 0;
              const paid = Number(ln.paid_amount) || 0;
              const remaining = Math.max(total - paid, 0);
              const isBorrow = ln.direction === "borrowed";
              return (
                <tr key={ln.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`finans-row-${ln.id}`}>
                  <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{formatDate(ln.date)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${
                      isBorrow ? "bg-rose-50 text-rose-800 border-rose-200" : "bg-emerald-50 text-emerald-800 border-emerald-200"
                    }`}>
                      {isBorrow ? "Aldık" : "Verdik"}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-semibold text-slate-900">{ln.counterparty || "-"}</td>
                  <td className="px-4 py-3 text-right font-mono-num font-semibold text-slate-900">{formatTRY(total)}</td>
                  <td className="px-4 py-3 text-right font-mono-num text-emerald-700">{formatTRY(paid)}</td>
                  <td className={`px-4 py-3 text-right font-mono-num font-semibold ${remaining > 0 ? "text-rose-700" : "text-slate-400"}`}>
                    {formatTRY(remaining)}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate">{ln.note || "-"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button data-testid={`edit-loan-${ln.id}`} variant="ghost" size="icon" onClick={() => openEdit(ln)} className="h-8 w-8">
                        <Pencil className="w-4 h-4 text-slate-600" />
                      </Button>
                      <Button data-testid={`delete-loan-${ln.id}`} variant="ghost" size="icon" onClick={() => setDeleteId(ln.id)} className="h-8 w-8 hover:bg-rose-50">
                        <Trash2 className="w-4 h-4 text-rose-600" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Form Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="finans-dialog" className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Landmark className="w-5 h-5 text-slate-700" />
              {editing ? "Kayıt Düzenle" : (form.direction === "borrowed" ? "Yeni Alınan Borç" : "Yeni Verilen Borç")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Yön</Label>
              <Select value={form.direction} onValueChange={(v) => setField("direction", v)}>
                <SelectTrigger data-testid="loan-direction-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="borrowed">Borç Aldık (Bize Verildi)</SelectItem>
                  <SelectItem value="lent">Borç Verdik (Biz Verdik)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Kişi / Firma</Label>
              <Input data-testid="loan-counterparty-input" value={form.counterparty}
                onChange={(e) => setField("counterparty", e.target.value)}
                placeholder="Örn: Ahmet Bey, XYZ Otomotiv" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Toplam Tutar (₺)</Label>
                <Input data-testid="loan-amount-input" type="number" step="0.01" value={form.amount}
                  onChange={(e) => setField("amount", e.target.value)} className="font-mono-num" />
              </div>
              <div>
                <Label>Tarih</Label>
                <Input data-testid="loan-date-input" type="date" value={form.date?.slice(0, 10)}
                  onChange={(e) => setField("date", e.target.value)} />
              </div>
            </div>

            <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
              <div className="flex items-center gap-2 mb-2">
                <input
                  data-testid="loan-fully-paid-checkbox"
                  type="checkbox" id="loan-fully-paid"
                  checked={form.fully_paid}
                  onChange={(e) => setField("fully_paid", e.target.checked)}
                  className="w-4 h-4 accent-emerald-600"
                />
                <Label htmlFor="loan-fully-paid" className="text-sm cursor-pointer">
                  {form.direction === "borrowed" ? "Tamamı geri ödendi" : "Tamamı geri alındı"}
                </Label>
              </div>
              {!form.fully_paid && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                      {form.direction === "borrowed" ? "Ödenen Kısım (₺)" : "Geri Alınan (₺)"}
                    </Label>
                    <Input data-testid="loan-paid-input" type="number" step="0.01" value={form.paid_amount}
                      onChange={(e) => setField("paid_amount", e.target.value)} className="font-mono-num h-9 text-sm bg-white" />
                  </div>
                  <div className="flex flex-col justify-end">
                    <p className="text-[11px] text-slate-500">Kalan</p>
                    <p className="font-mono-num font-bold text-rose-700 text-base">
                      ₺{Math.max((Number(form.amount) || 0) - (Number(form.paid_amount) || 0), 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label>Not</Label>
              <Input data-testid="loan-note-input" value={form.note} onChange={(e) => setField("note", e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button data-testid="loan-save-btn" onClick={save} className="bg-slate-900 hover:bg-slate-800">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kaydı sil?</AlertDialogTitle>
            <AlertDialogDescription>Bu işlem geri alınamaz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-loan" onClick={doDelete} className="bg-rose-600 hover:bg-rose-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
